import { Directive, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { MobileContextService } from '@core/context/services/mobile-context.service';
import { ContextoPWA } from '@shared/types';

/**
 * Classe base abstrata para componentes de listagem contextual do PWA.
 *
 * Encapsula o boilerplate comum a todas as telas de listagem:
 *  - Assinatura do `contexto$` (carga inicial + troca passiva de contexto)
 *  - Assinatura do `contextChanged$` (refresh forçado ao confirmar o modal)
 *  - Deduplicação de contexto via chave serializada
 *  - Gerenciamento de paginação (`offset`, `limit`, `temMaisResultados`)
 *  - Estados de carregamento (`carregando`, `carregandoMais`)
 *  - Ciclo de vida RxJS via `destroy$`
 *
 * @template T Modelo da entidade da lista (ex: `PlanejamentoAula`, `Frequencia`).
 *
 * @example
 * ```typescript
 * @Component({ ... })
 * export class MinhaListaComponent extends AbstractContextualList<MinhaEntidade> {
 *   constructor(mobileContextService: MobileContextService) {
 *     super(mobileContextService);
 *   }
 *
 *   protected carregarDadosApi(isLoadMore: boolean, contexto: ContextoPWA): void {
 *     // lógica de chamada à API
 *   }
 * }
 * ```
 */
@Directive()
export abstract class AbstractContextualList<T> implements OnInit, OnDestroy {

  protected contexto: ContextoPWA | null = null;

  semContexto = false;

  registros: T[] = [];
  offset = 0;
  readonly limit = 20;
  carregando = false;
  carregandoMais = false;
  temMaisResultados = false;

  protected readonly destroy$ = new Subject<void>();

  /** Chave serializada do último contexto carregado — evita recargas duplicadas. */
  private _ultimoContextoKey: string | null = null;


  constructor(protected readonly mobileContextService: MobileContextService) {}

  ngOnInit(): void {
    const contextoInicial = this.mobileContextService.getContextoAtual();
    this._ultimoContextoKey = this._getContextoKey(contextoInicial);
    this._handleContextChange(contextoInicial);

    this.mobileContextService.contexto$
      .pipe(takeUntil(this.destroy$))
      .subscribe((contexto) => {
        const key = this._getContextoKey(contexto);
        if (key === this._ultimoContextoKey) { return; }
        this._ultimoContextoKey = key;
        this._handleContextChange(contexto);
      });

    this.mobileContextService.contextChanged$.pipe(
      debounceTime(150),
      takeUntil(this.destroy$)
    ).subscribe((novoContexto) => {
      this._ultimoContextoKey = this._getContextoKey(novoContexto);
      this._handleContextChange(novoContexto);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Carrega a próxima página de resultados. Chamado pelo botão "Mais resultados". */
  carregarMaisResultados(): void {
    if (!this.contexto || this.semContexto) { return; }
    this.offset += this.limit;
    this.carregarDadosApi(true, this.contexto);
  }

  protected abstract carregarDadosApi(isLoadMore: boolean, contexto: ContextoPWA): void;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function -- hook opcional, subclasses sobrescrevem
  protected aoContextoMudar(_contexto: ContextoPWA | null): void {}

  /**
   * Reseta paginação e lista e dispara uma nova carga da primeira página.
   * Use nos handlers de filtro da subclasse.
   */
  protected recarregar(): void {
    if (!this.contexto || this.semContexto) { return; }
    this._resetarPaginacao();
    this.carregarDadosApi(false, this.contexto);
  }

  private _handleContextChange(contexto: ContextoPWA | null): void {
    this.contexto    = contexto;
    this.semContexto = !contexto?.turma?.id || !contexto?.servidor?.id;

    this.aoContextoMudar(contexto);
    this._resetarPaginacao();

    if (this.semContexto) {
      this.carregando = false;
      return;
    }

    this.carregarDadosApi(false, contexto!);
  }

  private _resetarPaginacao(): void {
    this.offset            = 0;
    this.registros         = [];
    this.temMaisResultados = false;
  }

  private _getContextoKey(contexto: ContextoPWA | null): string | null {
    if (!contexto?.turma?.id) { return null; }
    return JSON.stringify({
      servidorId:        contexto.servidor?.id        ?? null,
      anoLetivoId:       contexto.anoLetivo?.id       ?? null,
      estabelecimentoId: contexto.estabelecimento?.id ?? null,
      turmaId:           contexto.turma.id,
      entidadeId:        contexto.entidade?.id        ?? null
    });
  }
}

